import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogoLockup } from '@/components/primitives/LogoLockup';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Menu, X, Zap, MapPin, ChevronDown } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { canadianCities, getAllProvinces, getCitiesByProvince } from '@/data/cities';

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const provinces = getAllProvinces();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'How it works', href: '/#how-it-works' },
    { label: 'For homeowners', href: '/#homeowners' },
    { label: 'For operators', href: '/operators', badge: 'Hiring' },
    { label: 'Storm Pass', href: '/storm-pass', badge: 'New' },
    { label: 'FAQ', href: '/faq' },
  ];

  const handleGetApp = () => {
    trackEvent('header_cta_click', { cta_type: 'get_app' });
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-xl border-b border-neutral-200/80 shadow-lg' 
            : 'bg-white/80 backdrop-blur-sm border-b border-neutral-100'
        }`}
      >
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 hover:scale-105 transition-transform duration-200">
              <LogoLockup size="sm" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-bold text-neutral-700 hover:text-brand-500 transition-colors relative group py-2"
                >
                  <span className="flex items-center gap-2">
                    {link.label}
                    {link.badge && (
                      <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full ${
                        link.badge === 'New' 
                          ? 'bg-accent-500/10 text-accent-500' 
                          : 'bg-brand-500/10 text-brand-500'
                      }`}>
                        {link.badge}
                      </span>
                    )}
                  </span>
                  <span className="absolute -bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-500 to-accent-500 transition-all duration-300 group-hover:w-full" />
                </a>
              ))}
              
              {/* Cities Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setCitiesOpen(true)}
                onMouseLeave={() => setCitiesOpen(false)}
              >
                <button className="text-sm font-bold text-neutral-700 hover:text-brand-500 transition-colors relative group py-2 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Cities
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${citiesOpen ? 'rotate-180' : ''}`} />
                  <span className="absolute -bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-500 to-accent-500 transition-all duration-300 group-hover:w-full" />
                </button>

                <AnimatePresence>
                  {citiesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl border-2 border-neutral-200 p-6 z-50"
                      style={{ maxHeight: '80vh', overflowY: 'auto' }}
                    >
                      <div className="mb-4 pb-4 border-b border-neutral-200">
                        <h3 className="text-lg font-black text-neutral-900 mb-1">Select Your City</h3>
                        <p className="text-sm text-neutral-600 font-medium">Available across Canada</p>
                      </div>

                      <div className="space-y-6">
                        {provinces.slice(0, 4).map((province) => {
                          const provinceCities = getCitiesByProvince(
                            canadianCities.find(c => c.province === province)?.provinceCode || ''
                          ).slice(0, 6); // Limit to 6 cities per province in dropdown

                          if (provinceCities.length === 0) return null;

                          return (
                            <div key={province}>
                              <div className="text-xs font-black text-brand-500 uppercase tracking-wider mb-2">
                                {provinceCities[0].provinceCode}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {provinceCities.map((city) => (
                                  <Link
                                    key={city.slug}
                                    to={`/cities/${city.slug}`}
                                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 transition-colors group"
                                    onClick={() => {
                                      setCitiesOpen(false);
                                      trackEvent('header_city_click', { city: city.name });
                                    }}
                                  >
                                    <MapPin className="w-4 h-4 text-brand-500 flex-shrink-0" />
                                    <span className="text-sm font-bold text-neutral-700 group-hover:text-brand-500 transition-colors">
                                      {city.name}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-6 pt-4 border-t border-neutral-200">
                        <Link
                          to="/cities"
                          className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-brand-500 to-accent-500 text-white font-black rounded-xl hover:shadow-lg transition-all"
                          onClick={() => {
                            setCitiesOpen(false);
                            trackEvent('header_view_all_cities');
                          }}
                        >
                          View All {canadianCities.length} Cities
                          <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            {/* CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setLoginOpen(true)} 
                className="font-bold hover:bg-neutral-100 transition-all"
              >
                Log in
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleGetApp} 
                className="bg-brand-500 hover:bg-brand-600 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 font-black relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" fill="currentColor" />
                  Get the app
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-accent-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2.5 text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {mobileOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="lg:hidden border-t border-neutral-200 bg-white/98 backdrop-blur-xl shadow-2xl overflow-hidden"
            >
              <nav className="flex flex-col p-6 space-y-4">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="text-lg font-bold text-neutral-700 hover:text-brand-500 transition-colors py-2 flex items-center justify-between group"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span>{link.label}</span>
                    {link.badge && (
                      <span className={`px-2.5 py-1 text-xs font-black uppercase tracking-wider rounded-full ${
                        link.badge === 'New' 
                          ? 'bg-accent-500/10 text-accent-500' 
                          : 'bg-brand-500/10 text-brand-500'
                      }`}>
                        {link.badge}
                      </span>
                    )}
                    <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                  </motion.a>
                ))}
                
                {/* Cities Link in Mobile */}
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: navLinks.length * 0.05 }}
                >
                  <Link
                    to="/cities"
                    className="text-lg font-bold text-neutral-700 hover:text-brand-500 transition-colors py-2 flex items-center gap-2 group"
                    onClick={() => setMobileOpen(false)}
                  >
                    <MapPin className="w-5 h-5" />
                    <span>Cities</span>
                    <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                  </Link>
                </motion.div>
                
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: (navLinks.length + 1) * 0.05 }}
                  className="pt-6 border-t border-neutral-200 flex flex-col gap-3"
                >
                  <Button 
                    variant="ghost" 
                    size="md" 
                    className="w-full font-bold justify-center" 
                    onClick={() => {
                      setLoginOpen(true);
                      setMobileOpen(false);
                    }}
                  >
                    Log in
                  </Button>
                  <Button 
                    variant="primary" 
                    size="md" 
                    className="w-full bg-gradient-to-r from-brand-500 to-accent-500 hover:shadow-lg font-black" 
                    onClick={() => {
                      handleGetApp();
                      setMobileOpen(false);
                    }}
                  >
                    <Zap className="w-4 h-4 mr-2" fill="currentColor" />
                    Get the app
                  </Button>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Login Modal Placeholder */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Coming Soon</DialogTitle>
          </DialogHeader>
          <p className="text-neutral-700 font-medium leading-relaxed">
            Web login is coming soon. For now, download the SNOWBLOWR app to get started and access your account!
          </p>
          <Button variant="primary" onClick={() => {
            setLoginOpen(false);
            handleGetApp();
          }} className="w-full">
            Get the app
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

